import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AppParametricSelector, makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {
  TableOffsetPaginationDataModel,
  TablePaginationTypesEnum,
} from '../../../../models/table/tablePaginationDataModel';
import {SettingsDevelopersApplicationServersUrl} from '../../../../models/url/settingsUrlModel';
import {AppState} from '../../../../store/appState';
import {makeGetLocalApplication} from '../../../../store/data/localApplications/localApplicationsSelectors';
import {DevelopersServersTable} from './developersServersTable';

/*
 * Props.
 */

interface DevelopersServersTableContainerProps {
  url: SettingsDevelopersApplicationServersUrl;
}

/*
 * Selectors.
 */

function getApplicationIdFromUrl(_state: AppState, props: DevelopersServersTableContainerProps) {
  return props.url.applicationId;
}

function makeGetApplicationServers(getApplicationId: AppParametricSelector<any, number>) {
  return createSelector(makeGetLocalApplication(getApplicationId), (application) => application?.servers);
}

function makeGetServerIds(getApplicationServers: ReturnType<typeof makeGetApplicationServers>) {
  return createSelector(getApplicationServers, (servers) => (servers?.items ?? []).map((s) => s.id));
}

function makeGetPaginationData(getApplicationServers: ReturnType<typeof makeGetApplicationServers>) {
  return createSelector(getApplicationServers, (servers) => {
    if (!servers || !servers.items) {
      return undefined;
    }
    const initialPaginationData: TableOffsetPaginationDataModel = {
      type: TablePaginationTypesEnum.OFFSET,
      // The endpoint currently doesn't support paging.
      canLoadMore: false,
      offset: (servers.items ?? []).length,
    };
    return initialPaginationData;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<DevelopersServersTableContainerProps>()(() => {
  const getApplicationServers = makeGetApplicationServers(getApplicationIdFromUrl);
  return {
    serverIds: makeGetServerIds(getApplicationServers),
    paginationData: makeGetPaginationData(getApplicationServers),
  };
});

export const DevelopersServersTableContainer = connect(mapStateToProps)(DevelopersServersTable);
